import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, Command as CommandIcon, CornerDownLeft, ArrowUp, ArrowDown,
  LayoutDashboard, Folder, ListTodo, Shield, BookOpen, Brain, Settings,
  FileText, Trash2, Plug, Keyboard,
} from 'lucide-react'

/**
 * A11.4 — Command palette + keyboard shortcut layer.
 *
 * Opens on Cmd/Ctrl+Shift+P (Cmd/Ctrl+K is owned by GlobalSearch). Provides
 * keyboard-driven navigation and actions. A "g"-prefixed shortcut layer lets
 * power users jump between sections without the palette (e.g. `g` then `p` →
 * projects). Pressing `?` opens the shortcut cheat-sheet.
 */
interface Cmd {
  id: string
  label: string
  hint?: string
  icon: React.ComponentType<{ className?: string }>
  run: () => void
  keywords?: string
}

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null
  if (!node) return false
  const tag = node.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable
}

export function CommandPalette() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const commands: Cmd[] = useMemo(() => {
    const go = (path: string) => () => { setOpen(false); navigate(path) }
    return [
      { id: 'home', label: 'Go to My Day', icon: LayoutDashboard, run: go('/home'), keywords: 'today start' },
      { id: 'dashboard', label: 'Go to Dashboard', icon: LayoutDashboard, run: go('/dashboard') },
      { id: 'projects', label: 'Go to Projects', icon: Folder, run: go('/project'), hint: 'g p' },
      { id: 'tasks', label: 'Go to Tasks', icon: ListTodo, run: go('/task'), hint: 'g t' },
      { id: 'governance', label: 'Go to Governance', icon: Shield, run: go('/governance') },
      { id: 'lessons', label: 'Go to Lessons Learned', icon: BookOpen, run: go('/lessons') },
      { id: 'ai', label: 'Go to AI Assistant', icon: Brain, run: go('/ai') },
      { id: 'templates', label: 'Open Template Editor', icon: FileText, run: go('/templates'), keywords: 'admin' },
      { id: 'integrations', label: 'Open Integration Breadth', icon: Plug, run: go('/integrations/breadth') },
      { id: 'recycle', label: 'Open Recycle Bin', icon: Trash2, run: go('/recycle-bin'), keywords: 'deleted restore' },
      { id: 'settings', label: 'Open Admin & Settings', icon: Settings, run: go('/settings') },
      { id: 'new-project', label: 'Create new Project', icon: Folder, run: go('/project/create'), keywords: 'add' },
      { id: 'new-task', label: 'Create new Task', icon: ListTodo, run: go('/task/create'), keywords: 'add' },
      { id: 'shortcuts', label: 'Show keyboard shortcuts', icon: Keyboard, run: () => { setOpen(false); setHelpOpen(true) }, hint: '?' },
    ]
  }, [navigate])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    return commands.filter(c =>
      c.label.toLowerCase().includes(q) || (c.keywords ?? '').toLowerCase().includes(q)
    )
  }, [commands, query])

  // Open / close + the "g"-prefixed nav layer + "?" help.
  useEffect(() => {
    let pendingG = false
    let gTimer: ReturnType<typeof setTimeout> | undefined

    const handler = (e: KeyboardEvent) => {
      // Open palette: Cmd/Ctrl+Shift+P
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault()
        setOpen(o => !o)
        return
      }
      if (e.key === 'Escape') {
        setOpen(false); setHelpOpen(false); return
      }
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return

      // "?" → shortcut cheat-sheet
      if (e.key === '?') { e.preventDefault(); setHelpOpen(h => !h); return }

      // "g" then a letter → jump
      if (pendingG) {
        pendingG = false
        if (gTimer) clearTimeout(gTimer)
        const map: Record<string, string> = {
          p: '/project', t: '/task', d: '/dashboard', h: '/home',
          g: '/governance', l: '/lessons', a: '/ai',
        }
        const dest = map[e.key.toLowerCase()]
        if (dest) { e.preventDefault(); navigate(dest) }
        return
      }
      if (e.key.toLowerCase() === 'g') {
        pendingG = true
        gTimer = setTimeout(() => { pendingG = false }, 800)
      }
    }
    document.addEventListener('keydown', handler)
    return () => { document.removeEventListener('keydown', handler); if (gTimer) clearTimeout(gTimer) }
  }, [navigate])

  useEffect(() => {
    if (open) {
      setQuery(''); setActive(0)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  useEffect(() => { setActive(0) }, [query])

  const onListKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); filtered[active]?.run() }
  }, [filtered, active])

  if (!open && !helpOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-black/40"
      onClick={() => { setOpen(false); setHelpOpen(false) }}
    >
      {open && (
        <div
          className="w-full max-w-xl bg-white rounded-lg shadow-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 px-4 border-b border-gray-200">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onListKey}
              placeholder="Type a command or search…"
              className="flex-1 py-3 text-sm outline-none"
            />
            <kbd className="text-xs text-gray-400 flex items-center gap-1">
              <CommandIcon className="w-3 h-3" />⇧P
            </kbd>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-gray-400">No matching commands</li>
            )}
            {filtered.map((c, i) => {
              const Icon = c.icon
              return (
                <li key={c.id}>
                  <button
                    onMouseEnter={() => setActive(i)}
                    onClick={() => c.run()}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left ${
                      i === active ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">{c.label}</span>
                    {c.hint && <kbd className="text-xs text-gray-400">{c.hint}</kbd>}
                  </button>
                </li>
              )
            })}
          </ul>
          <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
            <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> navigate</span>
            <span className="flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> select</span>
            <span>esc to close</span>
          </div>
        </div>
      )}

      {helpOpen && !open && (
        <div
          className="w-full max-w-md bg-white rounded-lg shadow-2xl p-5"
          onClick={e => e.stopPropagation()}
        >
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Keyboard className="w-4 h-4" /> Keyboard shortcuts
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            {[
              ['⌘/Ctrl ⇧ P', 'Open command palette'],
              ['⌘/Ctrl K', 'Open global search'],
              ['g then p', 'Go to Projects'],
              ['g then t', 'Go to Tasks'],
              ['g then d', 'Go to Dashboard'],
              ['g then h', 'Go to My Day'],
              ['g then g', 'Go to Governance'],
              ['g then l', 'Go to Lessons'],
              ['g then a', 'Go to AI Assistant'],
              ['?', 'Toggle this help'],
            ].map(([k, label]) => (
              <li key={k} className="flex justify-between">
                <span>{label}</span>
                <kbd className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{k}</kbd>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
